import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  FormControlLabel,
  CircularProgress,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

interface Role {
  id: number;
  name: string;
  rights: string[];
}

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [modifiedRoles, setModifiedRoles] = useState<Role[]>([]);
  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [newRoleName, setNewRoleName] = useState<string>('');
  const { user } = useAuth();

  // Define permission types
  type PermissionType = {
    name: string;
    label: string;
    read: string;
    write: string;
  };

  // List of all possible permissions with read/write rights
  const permissions: PermissionType[] = [
    {
      name: 'users',
      label: 'Users',
      read: 'view_users',
      write: 'manage_users'
    },
    {
      name: 'visitors',
      label: 'Visitors',
      read: 'view_visitors',
      write: 'manage_visitors'
    },
    {
      name: 'settings',
      label: 'Settings',
      read: 'view_settings',
      write: 'manage_settings'
    },
    {
      name: 'reports',
      label: 'Reports',
      read: 'view_reports',
      write: 'edit_reports'
    },
    {
      name: 'system',
      label: 'System',
      read: 'view_system',
      write: 'edit_system'
    }
  ];

  // Flat list of all rights for internal use
  const allRights = permissions.flatMap(p => [p.read, p.write]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      // Try both API paths
      let response;
      try {
        console.log('Attempting to fetch roles from /api/users/roles/all');
        response = await api.get('/api/users/roles/all');
        console.log('Roles fetched successfully:', response.data);
      } catch (err) {
        console.log('First attempt failed, trying alternative path');
        response = await api.get('/users/roles/all');
        console.log('Roles fetched successfully from alternative path:', response.data);
      }
      
      setRoles(response.data);
      setModifiedRoles(JSON.parse(JSON.stringify(response.data))); // Deep copy
    } catch (err: any) {
      console.error('Error fetching roles:', err);
      setError(err.response?.data?.message || 'Failed to fetch roles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRightChange = (roleId: number, right: string, checked: boolean) => {
    console.log(`Changing right ${right} for role ${roleId} to ${checked ? 'checked' : 'unchecked'}`);
    
    // Create a deep copy of the current roles to avoid reference issues
    const updatedRoles = modifiedRoles.map(role => {
      if (role.id === roleId) {
        const updatedRights = [...role.rights]; // Create a copy of the rights array
        
        if (checked && !updatedRights.includes(right)) {
          // Add the right if it's checked and doesn't exist
          updatedRights.push(right);
        } else if (!checked && updatedRights.includes(right)) {
          // Remove the right if it's unchecked and exists
          const index = updatedRights.indexOf(right);
          if (index !== -1) {
            updatedRights.splice(index, 1);
          }
        }
        
        console.log(`Role ${roleId} (${role.name}) rights updated:`, updatedRights);
        return { ...role, rights: updatedRights };
      }
      return { ...role }; // Return a new object for other roles too
    });
    
    // Update the state with the new roles array
    setModifiedRoles(updatedRoles);
  };

  const hasRightsChanged = (roleId: number) => {
    const originalRole = roles.find(r => r.id === roleId);
    const modifiedRole = modifiedRoles.find(r => r.id === roleId);
    
    if (!originalRole || !modifiedRole) return false;
    
    // If the lengths are different, rights have definitely changed
    if (originalRole.rights.length !== modifiedRole.rights.length) return true;
    
    // Check if any rights in original are not in modified
    const originalHasRightsMissingInModified = originalRole.rights.some(right => 
      !modifiedRole.rights.includes(right)
    );
    
    // Check if any rights in modified are not in original
    const modifiedHasNewRights = modifiedRole.rights.some(right => 
      !originalRole.rights.includes(right)
    );
    
    return originalHasRightsMissingInModified || modifiedHasNewRights;
  };

  const saveRoleRights = async (roleId: number) => {
    const roleToUpdate = modifiedRoles.find(r => r.id === roleId);
    
    if (!roleToUpdate) return;
    
    try {
      // Make a deep copy of the rights array to avoid reference issues
      const rightsToSend = [...roleToUpdate.rights];
      
      console.log(`Saving role ${roleId} (${roleToUpdate.name}) with rights:`, rightsToSend);
      
      // Try the API call with a simple approach
      let response;
      try {
        response = await api.put(`/api/users/roles/${roleId}/rights`, { 
          rights: rightsToSend 
        });
        console.log('Role rights updated successfully:', response.data);
      } catch (err) {
        console.log('First attempt failed, trying alternative path');
        response = await api.put(`/users/roles/${roleId}/rights`, { 
          rights: rightsToSend 
        });
        console.log('Role rights updated successfully from alternative path:', response.data);
      }
      
      // Update success message
      setSuccess(`Rights for ${roleToUpdate.name} role updated successfully`);
      
      // Update the original roles array with the saved changes
      setRoles(prevRoles => 
        prevRoles.map(role => 
          role.id === roleId ? { ...role, rights: [...rightsToSend] } : role
        )
      );
      
      // Ensure the modified roles match the saved state
      setModifiedRoles(prevRoles => 
        prevRoles.map(role => 
          role.id === roleId ? { ...role, rights: [...rightsToSend] } : role
        )
      );
      
      // Force a refresh of the roles after a short delay
      setTimeout(() => {
        fetchRoles();
      }, 1000);
    } catch (err: any) {
      console.error('Error updating role rights:', err);
      setError(err.response?.data?.message || 'Failed to update role rights. Please try again.');
    }
  };

  const handleAddRole = async () => {
    if (!newRoleName.trim()) {
      setError('Role name cannot be empty');
      return;
    }

    try {
      console.log('Adding new role:', newRoleName);
      
      // Try both API paths
      let response;
      try {
        response = await api.post('/api/users/roles', { 
          name: newRoleName,
          rights: [] // New role starts with no permissions
        });
        console.log('Role added successfully:', response.data);
      } catch (err) {
        console.log('First attempt failed, trying alternative path');
        response = await api.post('/users/roles', { 
          name: newRoleName,
          rights: [] 
        });
        console.log('Role added successfully from alternative path:', response.data);
      }
      
      // Add the new role to the state
      const newRole = response.data;
      setRoles(prevRoles => [...prevRoles, newRole]);
      setModifiedRoles(prevRoles => [...prevRoles, newRole]);
      
      setSuccess(`Role '${newRoleName}' added successfully`);
      setNewRoleName('');
      setOpenAddDialog(false);
    } catch (err: any) {
      console.error('Error adding role:', err);
      setError(err.response?.data?.message || 'Failed to add role. Please try again.');
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;
    
    try {
      console.log(`Deleting role ${roleToDelete.id}`);
      
      // Try both API paths
      let response;
      try {
        response = await api.delete(`/api/users/roles/${roleToDelete.id}`);
        console.log('Role deleted successfully:', response.data);
      } catch (err) {
        console.log('First attempt failed, trying alternative path');
        response = await api.delete(`/users/roles/${roleToDelete.id}`);
        console.log('Role deleted successfully from alternative path:', response.data);
      }
      
      // Remove the role from the state
      setRoles(prevRoles => prevRoles.filter(role => role.id !== roleToDelete.id));
      setModifiedRoles(prevRoles => prevRoles.filter(role => role.id !== roleToDelete.id));
      
      setSuccess(`Role '${roleToDelete.name}' deleted successfully`);
      setRoleToDelete(null);
      setOpenDeleteDialog(false);
    } catch (err: any) {
      console.error('Error deleting role:', err);
      setError(err.response?.data?.message || 'Failed to delete role. Please try again.');
    }
  };

  const handleOpenDeleteDialog = (role: Role) => {
    setRoleToDelete(role);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setRoleToDelete(null);
  };

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewRoleName('');
  };

  const handleCloseAlert = () => {
    setError(null);
    setSuccess(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (user?.role?.toLowerCase() !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          You do not have permission to access this page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <SecurityIcon sx={{ mr: 1 }} /> Role Management
        </Typography>
        
        <Button
          variant="contained"
          color="success"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          Add New Role
        </Button>
      </Box>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        Manage the permissions and rights assigned to each role in the system.
      </Typography>
      
      {modifiedRoles.map(role => (
        <Paper elevation={3} sx={{ mb: 3 }} key={role.id}>
          <Box sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            p: 2,
            borderRadius: '4px 4px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box>
              <Typography variant="h6">{role.name}</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                ID: {role.id}
              </Typography>
            </Box>
            {role.id > 3 && (
              <Tooltip title="Delete Role">
                <IconButton 
                  color="error" 
                  onClick={() => handleOpenDeleteDialog(role)}
                  sx={{ color: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' } }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Permissions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Permission</TableCell>
                    <TableCell align="center">Read</TableCell>
                    <TableCell align="center">Write</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {permissions.map(permission => (
                    <TableRow key={permission.name}>
                      <TableCell>{permission.label}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={modifiedRoles.find(r => r.id === role.id)?.rights.includes(permission.read) || false}
                          onChange={(e) => handleRightChange(role.id, permission.read, e.target.checked)}
                          name={permission.read}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={modifiedRoles.find(r => r.id === role.id)?.rights.includes(permission.write) || false}
                          onChange={(e) => handleRightChange(role.id, permission.write, e.target.checked)}
                          name={permission.write}
                          color="secondary"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="primary"
                disabled={!hasRightsChanged(role.id)}
                onClick={() => saveRoleRights(role.id)}
              >
                Save Changes
              </Button>
            </Box>
          </Box>
        </Paper>
      ))}
      
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Add Role Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog}>
        <DialogTitle>Add New Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a name for the new role. You can assign permissions after creating it.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Role Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button onClick={handleAddRole} variant="contained" color="primary">
            Add Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Role Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the role "{roleToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteRole} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
